import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Plus } from 'lucide-react';
import { useVideoLessons } from './hooks/useVideoLessons';
import { VideoLessonDialog, VideoLessonsTable } from './components';
import { VideoLessonsPageProps } from './types';

export default function VideoLessonsPage({ isDark }: VideoLessonsPageProps) {
  const {
    videoLessons,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    isSubmitting,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  } = useVideoLessons();

  const formatDate = (value: string) => {
    if (!value) return '-';
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#111111]'
            )}
          >
            Видеоуроки
          </h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Управление видеоуроками
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
          <Plus className="w-4 h-4 mr-2" />
          Создать видеоурок
        </Button>
      </div>

      <Card
        className={cn(
          'rounded-2xl border-0 overflow-hidden transition-all duration-300',
          isDark ? 'bg-[#1A1A22] shadow-lg shadow-black/10' : 'bg-white shadow-lg shadow-slate-200/50'
        )}
      >
        <VideoLessonsTable
          isDark={isDark}
          videoLessons={videoLessons}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          formatDate={formatDate}
        />
      </Card>

      <VideoLessonDialog
        isDark={isDark}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        title="Создать видеоурок"
        description="Добавить новый видеоурок"
        submitLabel="Создать"
      />

      <VideoLessonDialog
        isDark={isDark}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        title="Редактировать видеоурок"
        description="Обновить видеоурок"
        submitLabel="Сохранить"
      />
    </div>
  );
}
